import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  NEVER,
  combineLatest,
  filter,
  map,
  race,
  switchMap,
  take,
  timer,
  withLatestFrom,
  type Observable,
} from 'rxjs';

export interface InitialLoadIdleOptions {
  quietWindowMs?: number;
  maxWaitMs?: number;
  sessionId?: number;
}

export const INITIAL_LOAD_IDLE_DEFAULTS = {
  quietWindowMs: 250,
  maxWaitMs: 8000,
} as const;

@Injectable({
  providedIn: 'root',
})
export class InitialDataLoaderService {
  private readonly pendingRequestsSubject = new BehaviorSubject<number>(0);
  private readonly trackingSessionSubject = new BehaviorSubject<number>(0);
  private trackingEnabled = false;

  startTracking(): void {
    this.trackingSessionSubject.next(this.trackingSessionSubject.value + 1);
    this.trackingEnabled = true;
    this.pendingRequestsSubject.next(0);
  }

  stopTracking(): void {
    this.trackingEnabled = false;
    this.pendingRequestsSubject.next(0);
  }

  isTrackingEnabled(): boolean {
    return this.trackingEnabled;
  }

  getTrackingSessionId(): number {
    return this.trackingSessionSubject.value;
  }

  onRequestStarted(): void {
    if (!this.trackingEnabled) {
      return;
    }

    this.pendingRequestsSubject.next(this.pendingRequestsSubject.value + 1);
  }

  onRequestFinished(): void {
    if (!this.trackingEnabled) {
      return;
    }

    this.pendingRequestsSubject.next(Math.max(0, this.pendingRequestsSubject.value - 1));
  }

  waitUntilSettledIdle(options?: InitialLoadIdleOptions): Observable<void> {
    const quietWindowMs = options?.quietWindowMs ?? INITIAL_LOAD_IDLE_DEFAULTS.quietWindowMs;
    const maxWaitMs = options?.maxWaitMs ?? INITIAL_LOAD_IDLE_DEFAULTS.maxWaitMs;
    const sessionId = options?.sessionId ?? this.getTrackingSessionId();

    const settledIdle$ = combineLatest([
      this.pendingRequestsSubject,
      this.trackingSessionSubject,
    ]).pipe(
      switchMap(([pendingRequestsCount, currentSessionId]) => {
        if (currentSessionId !== sessionId || pendingRequestsCount !== 0) {
          return NEVER;
        }

        return timer(quietWindowMs);
      }),
      take(1),
      map(() => void 0),
    );

    const timeoutFallback$ = timer(maxWaitMs).pipe(
      withLatestFrom(this.trackingSessionSubject),
      filter(([, currentSessionId]) => currentSessionId === sessionId),
      map(() => void 0),
    );

    return race(settledIdle$, timeoutFallback$).pipe(take(1));
  }
}
